import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import config from '../config';

const useFetchData = (entity, id, method = 'get', options = {}) => {
  const { url } = config;
  const {
    enabled = true,
    params,
    data: body,
    headers,
    onSuccess,
    onError,
  } = options;

  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (!entity || !enabled) return;

    const hasId = id !== undefined && id !== null && id !== '';

    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    if (isMountedRef.current) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await axios({
        method,
        url: `${url}api/${entity}${hasId ? '/' + id : ''}`,
        params,
        data: body,
        headers,
        withCredentials: true,
        signal: controller.signal,
      });

      if (isMountedRef.current) setData(response?.data);
      if (onSuccess) onSuccess(response?.data);
    } catch (err) {
      if (err?.name === 'CanceledError') return;
      if (isMountedRef.current) setError(err);
      if (onError) onError(err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [
    entity,
    id,
    method,
    url,
    enabled,
    params,
    body,
    headers,
    onSuccess,
    onError,
  ]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!entity || !enabled) return undefined;

    fetchData();

    return () => controllerRef.current?.abort();
  }, [entity, enabled, fetchData]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
  };
};

export default useFetchData;
